import { isSameDay } from 'date-fns'

// Historia 169 — piezas puras de la huella «Digital footprint in this job» (RN4-RN20, CA2-CA13), sin
// React (03-dominio.md §"Modelo de presentación (Jupiter)"). Formatean y clasifican lo que Sun ya
// calculó en D6/D6a/D6b; ninguna regla de negocio se recalcula aquí (D1). El veredicto de UX-11
// (`footprintVerdict`, S97) solo combina coberturas y estados que D6 ya publica.
//
// Enmienda UX — GATE 2 bis (2026-09-26): UX-3 (un solo formato de fecha, con año, en todo el panel;
// hora sola solo dentro del mismo día que la fila anterior de la misma lista) y UX-4 (nunca dos "—" en
// la misma línea) viven aquí como funciones puras.
//
// UX-10 (2026-09-26, decisión del PO tras revisar datos reales en mic1): las filas INTENTOS (D6c) y
// CONTEXTO (D6d) se retiraron de la huella — sus funciones puras exclusivas (`formatDayAwareWindow`,
// `scanAcceptance`, `failedTaskInfo`, `machineStateTotals`, `intervalsInWindow`, `clipIntervalsToWindow`,
// `mergeShortIntervals`) se han eliminado con ellas; lo que queda aquí es compartido con MATERIALES/
// INSPECCIÓN o con la frase de estado.

/**
 * RN7/CA2/UX-3: inicio → fin de una ventana, siempre con AÑO (`useDateFormatter().formatDateTime`); el
 * fin colapsa a solo la hora cuando cae el MISMO día que el inicio. Sin `endIso` (marca de cancelación,
 * S71) devuelve solo el instante de inicio, sin flecha. Sin `startIso`, ''.
 */
export const formatWhen = (startIso, endIso, formatter) => {
  if (!startIso) return ''
  const start = new Date(startIso)
  const startText = formatter.formatDateTime(start)
  if (!endIso) return startText
  const end = new Date(endIso)
  const endText = isSameDay(start, end) ? formatter.formatTime(end) : formatter.formatDateTime(end)
  return startText + ' → ' + endText
}

/**
 * UX-4/POL-F3/UX-G11 (corregido en UX-9/S95: la resolución original de los huecos ya decía "solo
 * cuando faltan A LA VEZ"/"con uno O DOS ausentes se omite CADA PAR", pero la primera implementación
 * fundía a partir de 2 ausentes cualesquiera — S95 lo destapa con un caso de 2 ausentes de 3 que antes
 * nunca se había ejercitado, worker presente + responsible/instrument ausentes). `presentFlags` es un
 * booleano por campo del mismo elemento (`true` = registrado):
 * - 0 ausentes → `ALL_PRESENT` (todo normal).
 * - 1 ausente → `ONE_MISSING` (el llamador omite ESE par rótulo/valor y deja un único icono `Absent`).
 * - más de uno pero no TODOS → `SOME_MISSING` (el llamador omite CADA par ausente, sin icono ni frase —
 *   nunca dos "—" porque cada par ausente simplemente no se pinta, S95).
 * - TODOS ausentes → `MERGE` (una sola frase sustituye a todos los pares ausentes de la línea).
 */
export const absenceMode = (presentFlags = []) => {
  const missingCount = presentFlags.filter((present) => !present).length
  if (missingCount === 0) return 'ALL_PRESENT'
  if (missingCount === presentFlags.length) return 'MERGE'
  if (missingCount === 1) return 'ONE_MISSING'
  return 'SOME_MISSING'
}

// UX-6/O-J5: las 4 claves de duración ya viven en el catálogo de las 8 locales, con el MISMO literal
// en las 8 hoy (convención de unidad científica: "s"/"min"/"h"/"d" no se traducen todavía) — hasta
// ahora `formatDuration` las reproducía a mano en vez de pasar por `t_()`, así que un idioma que algún
// día quisiera una abreviatura propia no podría: se decide aquí interpolarlas siempre por clave.
const interpolate = (key, params = {}) => Object.keys(params).reduce((text, name) => text.replace('{{' + name + '}}', params[name]), key)

/**
 * RN7/H15/UX-6: truncado a la unidad mostrada, sin cero a la izquierda en los minutos ("1 h 5 min").
 * `null`/`undefined`/negativo (fin < inicio, E-F2) devuelven '': el llamador decide el texto de aviso.
 * `t_` es OPCIONAL: sin él (uso puro/tests, o un llamador sin `useSmartTranslation` a mano) interpola
 * el propio literal de la clave, que hoy coincide byte a byte con las 8 locales; los llamadores con
 * `t_` disponible (todo componente) lo pasan para que las claves sigan siendo la fuente real.
 */
export const formatDuration = (ms, t_ = interpolate) => {
  if (ms === null || ms === undefined || ms < 0) return ''
  const totalSeconds = Math.floor(ms / 1000)
  if (totalSeconds < 60) return t_('{{value}} s', { value: totalSeconds })
  const totalMinutes = Math.floor(totalSeconds / 60)
  if (totalMinutes < 60) return t_('{{value}} min', { value: totalMinutes })
  const totalHours = Math.floor(totalMinutes / 60)
  if (totalHours < 24) return t_('{{hours}} h {{minutes}} min', { hours: totalHours, minutes: totalMinutes % 60 })
  const days = Math.floor(totalHours / 24)
  return t_('{{days}} d {{hours}} h', { days, hours: totalHours % 24 })
}

const RESULT_KEY = { OK: 'OK', FAIL: 'FAIL', PAUSED: 'PAUSED', NOTOOL: 'NOTOOL', CANCELLED: 'CANCELLED' }

/** RN6/CA3: el resultado registrado tal cual; sin resultado, "Sin resultado registrado". */
export const resultLabelKey = (result) => RESULT_KEY[result] || 'No result recorded'

/**
 * RN5/RN14/H17: la clave de CUÁNDO cuando no hay ejecución (`status` `NONE`/`NOT_STARTED`). Una pieza
 * ya `CANCELLED` (D6 `state`) nunca dice "Pendiente de iniciar" (H17): siempre "Sin ejecución registrada".
 */
export const emptyWhenKey = (execution = {}, partState = '') => {
  if (partState === 'CANCELLED') return 'No execution recorded'
  return execution.status === 'NOT_STARTED' ? 'Not started yet' : 'No execution recorded'
}

/** RN9/RN14/A2: sin máquina, según la ejecución esté en curso o cerrada; con máquina, ''. */
export const machineEmptyKey = (execution = {}, device) => {
  if (device) return ''
  if (execution.status === 'OPEN') return 'In progress without a recorded machine'
  if (execution.status === 'CLOSED') return 'Executed without a recorded machine'
  return ''
}

/** RN10/RN14: sin operarios en una ejecución en curso o cerrada, "Sin tramo de operario ni reporte". */
export const workerEmptyKey = (execution = {}, workers = []) => {
  if (execution.status !== 'OPEN' && execution.status !== 'CLOSED') return ''
  return (workers || []).length === 0 ? 'No operator span or report' : ''
}

/** RN11: el tooltip que explica la rama de deducción; `REGISTERED` (o ausente) no lleva marca. */
export const provenanceTooltipKey = (provenance) => {
  if (provenance === 'INFERRED_FROM_SPAN') return "Inferred from the operator's work span on this machine"
  if (provenance === 'INFERRED_FROM_REPORT') return 'Inferred from the quantity report closest to the end (±2 s)'
  return ''
}

/** RN11: todo lo que no sea `REGISTERED` se marca como deducido (POL-F1). */
export const isInferred = (provenance) => provenance === 'INFERRED_FROM_SPAN' || provenance === 'INFERRED_FROM_REPORT'

// H20 (2026-09-27, decisión editorial del hub, ratificación en GATE 3): con `count`/`total` 1 los
// resúmenes de MATERIALES/INSPECCIÓN (UX-5) y la línea de veredicto que los reutiliza (UX-11) pintaban
// "1 materiales"/"1 medidas"/"1 cotas sin medir" — convención del repo (como ya hacía
// `{{count}} open incidence`/`{{count}} open incidences`, UX-11): clave singular y plural separadas.
export const pluralize = (count, singularKey, pluralKey) => (count === 1 ? singularKey : pluralKey)

/**
 * RN12/RN15/CA6/UX-5: el resumen de MATERIALES en segmentos {key, params?, tone?} que el componente
 * une con " · " y traduce; nunca "0 of 0" (INV-F7: `NOT_APPLICABLE` con total 0 no es `NOT_RECORDED`).
 * UX-5/S67: la base del resumen depende solo de `mounted` (0 → "{{total}} material(s) in the job, none
 * mounted", singular con `total` 1, H20; > 0 → "{{mounted}} of {{total}} mounted"), la MISMA en
 * `NOT_RECORDED` y en `PARTIAL` — el panel ya no usa la clave "… · nothing recorded for this part".
 */
export const materialsSummary = (materials = {}) => {
  const { total = 0, mounted = 0, partial = 0, partComponents = 0, coverage = 'NOT_APPLICABLE' } = materials
  if (coverage === 'NOT_APPLICABLE') {
    return [{ key: partComponents > 0 ? 'Assembly of part components is not recorded' : 'No materials in this job' }]
  }
  const segments = []
  if (mounted === 0) {
    const key = pluralize(total, '{{total}} material in the job, none mounted', '{{total}} materials in the job, none mounted')
    segments.push({ key, params: { total } })
  } else {
    segments.push({ key: '{{mounted}} of {{total}} mounted', params: { mounted, total } })
  }
  if (partial > 0) segments.push({ key: '{{count}} partially', params: { count: partial } })
  if (partComponents > 0 && total > 0) {
    segments.push({ key: '{{count}} without consumption record', params: { count: partComponents } })
  }
  return segments
}

/**
 * RN13/RN15/D4/H1/H9/H16/CA6/UX-5 (UX-G1/UX-G2/UX-G9): el resumen de INSPECCIÓN en segmentos; el
 * muestreo aparte en tono neutro (D4), "fuera de tolerancia" en tono de error y "sin medir" en tono de
 * aviso (RN15). Cada segmento con contador solo se pinta si su contador es > 0 (UX-G9, generaliza H16).
 * `NOT_RECORDED` conserva la clave existente "{{count}} features not measured" (RN15 original, singular
 * "{{count}} feature not measured" con `count` 1, H20); con algo reportado (`reportedFeatures > 0`),
 * "{{count}} measured features" (UX-G1, cuenta = reportedFeatures; renombrada de "{{count}} measured"
 * y con su propio singular "{{count}} measured feature", H20) más "{{count}} out of tolerance"
 * (reutilizada, #160/UX-G2) y "{{count}} not measured" (nueva) según haga falta — ya no depende del
 * contador de `outOfTolerance`/`unmeasuredFeatures` a través del check de `reportedFeatures`, cada
 * segmento decide su propio contador.
 */
export const inspectionsSummary = (inspections = {}) => {
  const {
    features = 0,
    reportedFeatures = 0,
    outOfTolerance = 0,
    unmeasuredFeatures = 0,
    unmeasuredSampledFeatures = 0,
    coverage = 'NOT_APPLICABLE'
  } = inspections
  if (coverage === 'NOT_APPLICABLE') {
    return [{ key: features > 0 ? 'Only informative features' : 'No inspection features defined' }]
  }
  const segments = []
  if (coverage === 'NOT_RECORDED') {
    if (unmeasuredFeatures > 0) {
      const key = pluralize(unmeasuredFeatures, '{{count}} feature not measured', '{{count}} features not measured')
      segments.push({ key, params: { count: unmeasuredFeatures }, tone: 'warning' })
    }
  } else {
    if (reportedFeatures > 0) {
      const key = pluralize(reportedFeatures, '{{count}} measured feature', '{{count}} measured features')
      segments.push({ key, params: { count: reportedFeatures } })
    }
    if (outOfTolerance > 0) segments.push({ key: '{{count}} out of tolerance', params: { count: outOfTolerance }, tone: 'error' })
    if (unmeasuredFeatures > 0) segments.push({ key: '{{count}} not measured', params: { count: unmeasuredFeatures }, tone: 'warning' })
  }
  if (unmeasuredSampledFeatures > 0) {
    segments.push({ key: '{{count}} not measured due to sampling', params: { count: unmeasuredSampledFeatures }, tone: 'neutral' })
  }
  return segments
}

/** UX-1: los 5 "estados" posibles de la frase (RN4/RN5/H17), como clasificador puro y testeado —
 * evita repetir `hasExecution`/`isCancelledNoExecution` sueltos en el componente (O-J5). */
export const STATUS_LINE_KIND = {
  CLOSED: 'CLOSED',
  OPEN: 'OPEN',
  CANCELLATION_MARK: 'CANCELLATION_MARK',
  CANCELLED_EMPTY: 'CANCELLED_EMPTY',
  EMPTY: 'EMPTY'
}

export const statusLineKind = (execution = {}, partState = '') => {
  const status = execution.status || 'NONE'
  const hasExecution = status === 'OPEN' || status === 'CLOSED'
  if (hasExecution) {
    if (execution.cancellationMark) return STATUS_LINE_KIND.CANCELLATION_MARK
    return status === 'OPEN' ? STATUS_LINE_KIND.OPEN : STATUS_LINE_KIND.CLOSED
  }
  return partState === 'CANCELLED' ? STATUS_LINE_KIND.CANCELLED_EMPTY : STATUS_LINE_KIND.EMPTY
}

/**
 * UX-9 (historia 169): D6a no trae "pendiente" — se calcula aquí como requerido − consumido, igual que
 * la pestaña Materiales (#160 S72), NUNCA negativo (un sobreconsumo no resta, RN29/D25). `null` si falta
 * cualquiera de los dos operandos (el llamador decide el "—").
 */
export const materialPending = (line = {}) => {
  if (line.perPieceRequired === null || line.perPieceRequired === undefined) return null
  if (line.consumed === null || line.consumed === undefined) return null
  return Math.max(0, line.perPieceRequired - line.consumed)
}

/** UX-9/#160 D25: "Completo" (barra llena) cuando lo consumido cubre lo requerido, también con sobreconsumo. */
export const materialLineComplete = (line = {}) =>
  line.perPieceRequired !== null && line.perPieceRequired !== undefined &&
  line.consumed !== null && line.consumed !== undefined &&
  line.consumed >= line.perPieceRequired

// UX-11 (2026-09-27, decisión del PO, FIRMADO — S97): sustituye la cabecera "n intentos, m cierres
// manuales" (que contaba filas sembradas por #116, RN3 sin cambio) por una línea de veredicto arriba de
// la frase de estado — calculada SOLO con D6 base (sin llamada nueva), prioridad
// neutro > rojo > ámbar > verde.
export const VERDICT_TONE = { NEUTRAL: 'neutral', ERROR: 'error', WARNING: 'warning', SUCCESS: 'success' }

/**
 * UX-11: el veredicto de la pieza EN ESTE JOB.
 * - Neutro: sin ejecución (`NONE`/`NOT_STARTED`); H17 (pieza CANCELLED sin ejecución) → "Cancelada".
 * - Rojo: resultado FAIL, o alguna cota fuera de tolerancia (`inspections.outOfTolerance > 0`).
 * - Ámbar: materiales aplicables sin cubrir (`materials.coverage` distinto de COMPLETE/NOT_APPLICABLE),
 *   o cotas sin medir SIN contar el muestreo (`inspections.unmeasuredFeatures > 0`, D4), o alguna
 *   incidencia que afecta a la pieza sigue abierta (`status !== 'CLOSED'`); una ejecución OPEN sin
 *   ninguno de esos problemas también es ámbar ("En curso", reutiliza la clave existente).
 * - Verde: el resto (todo cubierto/medido/cerrado, ejecución CLOSED).
 */
export const footprintVerdict = (execution = {}, materials = {}, inspections = {}, incidences = [], partState = '') => {
  const status = execution.status || 'NONE'
  const hasExecution = status === 'OPEN' || status === 'CLOSED'

  if (!hasExecution) {
    if (partState === 'CANCELLED') return { tone: VERDICT_TONE.NEUTRAL, labelKey: 'Cancelled in this job' }
    return { tone: VERDICT_TONE.NEUTRAL, labelKey: 'No execution in this job' }
  }

  const hasFailedResult = execution.result === 'FAIL'
  const hasOutOfTolerance = (inspections.outOfTolerance || 0) > 0
  if (hasFailedResult || hasOutOfTolerance) {
    return { tone: VERDICT_TONE.ERROR, labelKey: 'Issues in this job' }
  }

  const materialsCovered = materials.coverage === 'COMPLETE' || materials.coverage === 'NOT_APPLICABLE'
  const hasUnmeasured = (inspections.unmeasuredFeatures || 0) > 0
  const hasOpenIncidence = (incidences || []).some((incidence) => incidence.status !== 'CLOSED')
  if (!materialsCovered || hasUnmeasured || hasOpenIncidence) {
    return { tone: VERDICT_TONE.WARNING, labelKey: 'Pending in this job' }
  }

  if (status === 'OPEN') return { tone: VERDICT_TONE.WARNING, labelKey: 'In progress' }

  return { tone: VERDICT_TONE.SUCCESS, labelKey: 'Complete in this job' }
}

/** UX-11: "Sin incidencias" (reutiliza "No incidences") o "{{count}} abierta(s) · {{count}} cerrada(s)",
 * solo el segmento que aplica (0 abiertas y 0 cerradas a la vez ya lo cubre "Sin incidencias"). */
export const verdictIncidencesSummary = (incidences = []) => {
  const open = (incidences || []).filter((incidence) => incidence.status !== 'CLOSED').length
  const closed = incidences.length - open
  if (open === 0 && closed === 0) return [{ key: 'No incidences' }]
  const segments = []
  if (open > 0) segments.push({ key: open === 1 ? '{{count}} open incidence' : '{{count}} open incidences', params: { count: open } })
  if (closed > 0) segments.push({ key: closed === 1 ? '{{count}} closed incidence' : '{{count}} closed incidences', params: { count: closed } })
  return segments
}

/** RN16/CA12: abiertas (cualquier estado ≠ `CLOSED`) primero, código descendente dentro de cada grupo. */
export const sortIncidences = (list = []) => {
  const isOpen = (incidence) => incidence.status !== 'CLOSED'
  return [...list].sort((left, right) => {
    const leftOpen = isOpen(left)
    const rightOpen = isOpen(right)
    if (leftOpen !== rightOpen) return leftOpen ? -1 : 1
    return String(right.code).localeCompare(String(left.code), undefined, { numeric: true })
  })
}
